import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode, useCallback, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { apiFetch, ApiProblem, onUnauthorized, SECOND_FACTOR_REQUIRED, type Me } from './lib/api.ts';
import { noteMeReceived } from './lib/business-time.ts';
import { createAuthClient, loadPublicConfig, type AuthClient, type LoginStep } from './lib/auth.ts';
import { SessionContext, type Session } from './lib/session.tsx';
import { clearPersonalCaches } from './lib/personal-caches.ts';
import { bindOrderFlowOwner } from './pages/order-flow.tsx';
import { LoginPage } from './pages/login.tsx';
import { SETUP_PATH, SetupPage } from './pages/setup.tsx';
import { prefetchListStart } from './components/list/list-api.ts';
import { pageList, preloadPage, router } from './router.tsx';
import './styles.css';

// Die aufgerufene Seite lädt zugleich mit der Anmeldung (Story 7.21), nicht erst danach.
if (window.location.pathname !== SETUP_PATH) preloadPage(window.location.pathname);

/** Große Liste der Startseite (einmal vorab geladen, siehe `prefetchListStart`). */
let startList = window.location.pathname === SETUP_PATH ? undefined : pageList(window.location.pathname);

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
});

type State =
  | { kind: 'loading' }
  | { kind: 'login'; step: Exclude<LoginStep, { kind: 'done' }> | null; message: string | null; auth: AuthClient | null }
  | { kind: 'ready'; session: Session };

function App() {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [auth, setAuth] = useState<AuthClient | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  // Einrichtungs-Link (`/einrichten#token_hash=…`): eigene Seite ohne Sitzung, bis das Passwort gesetzt ist.
  const [setup, setSetup] = useState(() => window.location.pathname === SETUP_PATH);
  // Hinweis „kein Zugang zum ERP“ überlebt das anschließende Abmelden.
  const lastMessage = useRef<string | null>(null);
  // Nur das Ergebnis der jüngsten Bewertung zählt (Abmelden während einer Abfrage).
  const run = useRef(0);

  useEffect(() => {
    loadPublicConfig()
      .then((config) => setAuth(createAuthClient(config)))
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Der Server ist gerade nicht erreichbar.';
        setConfigError(message);
        setState({ kind: 'login', step: null, auth: null, message });
      });
  }, []);

  const updateMe = useCallback((me: Me) => {
    noteMeReceived(me);
    setState((s) => (s.kind === 'ready' ? { kind: 'ready', session: { ...s.session, me } } : s));
  }, []);

  const evaluate = useCallback(async (client: AuthClient) => {
    const current = ++run.current;
    const apply = (next: State) => {
      if (current === run.current) setState(next);
    };
    let step: LoginStep;
    try {
      step = await client.currentStep();
    } catch (err) {
      // Weiche zur Anmeldung zurück, Sitzung verwerfen; Fehler als Satz zeigen.
      lastMessage.current = err instanceof Error ? err.message : String(err);
      await client.signOut();
      apply({ kind: 'login', step: { kind: 'password' }, auth: client, message: lastMessage.current });
      return;
    }
    if (step.kind !== 'done') {
      // Keine Daten der vorigen Sitzung für das nächste Konto aufheben.
      queryClient.clear();
      clearPersonalCaches();
      apply({ kind: 'login', step, auth: client, message: step.kind === 'password' ? lastMessage.current : null });
      return;
    }
    const getToken = () => client.getAccessToken();
    const toPassword = async (message: string) => {
      // Weiche zur Anmeldung zurück, Sitzung verwerfen; Grund als Satz zeigen.
      lastMessage.current = message;
      await client.signOut();
      apply({ kind: 'login', step: { kind: 'password' }, auth: client, message });
    };
    try {
      // Die Bewertung meldet ihr eigenes 401 nicht weiter (sonst Endlosschleife).
      const me = await apiFetch<Me>(getToken, '/api/v1/me', { notifyUnauthorized: false });
      noteMeReceived(me);
      // Liste der aufgerufenen Seite (Ansichten, Spalten) sofort laden, noch bevor die Oberfläche steht; nur beim ersten
      // Start und erst nach `/me` (sonst löste ein 401 vor dem zweiten Faktor eine weitere Bewertung aus).
      const list = startList;
      startList = undefined;
      if (list && current === run.current) prefetchListStart(queryClient, getToken, list);
      lastMessage.current = null;
      // Klärfall-Speicher (Story 7.20) gehört dieser Person; eine andere als zuvor beginnt leer.
      if (current === run.current) bindOrderFlowOwner(me.id);
      apply({ kind: 'ready', session: { me, auth: client, getToken, updateMe } });
    } catch (err) {
      if (err instanceof ApiProblem && err.status === 401 && err.code === SECOND_FACTOR_REQUIRED) {
        // Admin-Schalter „Zweiter Faktor“ ist an: Code eingeben oder App einrichten.
        let next: LoginStep;
        try {
          next = await client.secondFactorStep();
        } catch (stepErr) {
          await toPassword(stepErr instanceof Error ? stepErr.message : String(stepErr));
          return;
        }
        if (next.kind === 'done') {
          // Supabase meldet aal2, der Server nimmt den Ausweis trotzdem nicht: nicht im Kreis drehen.
          await toPassword(err.detail);
          return;
        }
        queryClient.clear();
        apply({ kind: 'login', step: next, auth: client, message: null });
      } else if (err instanceof ApiProblem && (err.status === 401 || err.status === 403)) {
        // Abgelaufen/ungültig (401) oder kein ERP-Zugang (403).
        await toPassword(err.detail);
      } else {
        apply({ kind: 'login', step: null, auth: client, message: err instanceof Error ? err.message : String(err) });
      }
    }
  }, [updateMe]);

  useEffect(() => {
    if (!auth || setup) return;
    void evaluate(auth);
    const offSignedOut = auth.onSignedOut(() => {
      // Nichts der vorigen Person im Browser lassen (Listen-Gedächtnis, Auswahl, Scrollstellen, Ansicht).
      clearPersonalCaches();
      void evaluate(auth);
    });
    // 401 mitten in der Arbeit (z. B. zweiter Faktor gerade eingeschaltet): neu bewerten.
    const offUnauthorized = onUnauthorized(() => void evaluate(auth));
    return () => {
      offSignedOut();
      offUnauthorized();
    };
  }, [auth, evaluate, setup]);

  const finishSetup = useCallback(() => {
    window.history.replaceState(null, '', '/');
    lastMessage.current = null;
    setSetup(false);
  }, []);

  const advance = useCallback(async () => {
    if (!auth) return;
    lastMessage.current = null;
    await evaluate(auth);
  }, [auth, evaluate]);

  if (setup) return <SetupPage auth={auth} configError={configError} onDone={finishSetup} />;
  if (state.kind === 'loading') return null;
  if (state.kind === 'login') {
    return <LoginPage step={state.step} message={state.message} auth={state.auth} onAdvance={advance} />;
  }
  return (
    <SessionContext.Provider value={state.session}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </SessionContext.Provider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
